import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import DeliveryLayout, { useDeliveryLayout } from '../layouts/DeliveryLayout';
import SearchBar from './components/SearchBar';
import DeliveryTabs, {DeliveryTab} from './components/DeliveryTabs';
import OrderCard, {DeliveringOrder} from './components/OrderCard';
import UploadNotification from "./components/UploadNotification";
import {useFailedUploadRetry} from "./hooks/useFailedUploadRetry";
import EmptyState from "../../../components/EmptyState";
import {
    preload,
    useExceptionDictionaries, useRetryBlockControl
} from "../store/deliveryStore";
import {BarCodeScanner} from "../../../components/BarCodeScanner";
import {Skeleton, Toast} from "antd-mobile";
import {TaskPath} from "../../../enums/DeliveryNavigatePathEnum";
import useHistoryControl from "../../../hooks/useHistoryControl";
import { useDeliveryTaskOrderList } from '../store/deliveryStore';
import {useNetwork} from "../../../hooks/useNetwork";
import PullToRefreshWrapper from "../../../components/PullToRefreshWrapper";
import {t} from "../../../i18n";
import { sortDeliveryOrders } from '../../../utils/stopNumberUtils';
import {useQuery} from "@apollo/client";
import {CHECK_SAME_AREA_ENABLED, CheckSameAreaEnabledResponse} from "../graphql/queries/checkSameAreaEnabled";

const Body: React.FC = () => {
    const location = useLocation();
    const [activeTab, setActiveTab] = useState<string>(location.state?.activeTab || 'delivering');
    const [orders, setOrders] = useState<DeliveringOrder[]>([]);
    const [searchValue, setSearchValue] = useState<string>(new URLSearchParams(location.search).get('trackingNumber') || '');
    const [uploadStatus, setUploadStatus] = useState<'retrying' | 'success' | 'none'>('none');
    const [deliveringCount, setDeliveringCount] = useState<number>(0);
    const [undeliveredCount, setUndeliveredCount] = useState<number>(0);
    const mainContainerRef = useRef<HTMLDivElement>(null);
    const { failedUploadCount, isRetrying, handleRetry, showRetryingHint } = useFailedUploadRetry();
    const { setRetryBlocked } = useRetryBlockControl();
    const exceptionDictionaries = useExceptionDictionaries();
    const { isOnline } = useNetwork();
    const [refreshing, setRefreshing] = useState(false);
    const { refreshFailedCount, podAlertHeight } = useDeliveryLayout();

    useHistoryControl(TaskPath.DELIVERY_TASK, {
        useReplace: true,
        controlNavButton: false
    });
    // 根据重试状态设置数据获取阻塞
    useEffect(() => {
        const shouldBlock = failedUploadCount > 0 || showRetryingHint;
        setRetryBlocked(shouldBlock);
    }, [failedUploadCount, isRetrying, setRetryBlocked]);

    const {deliveryTaskOrderList: deliveringTasks, loading, refreshDeliveryTaskOrderList} = useDeliveryTaskOrderList();
    
    // 查询是否启用同区域校验
    const { data: sameAreaEnabledData } = useQuery<CheckSameAreaEnabledResponse>(
        CHECK_SAME_AREA_ENABLED,
        {
            fetchPolicy: 'network-only'
        }
    );

    useEffect(() => {
        // 只有在重试数据初始化完成后才执行校验逻辑
        preload();
        const timer = setTimeout(() => {
            if (failedUploadCount <= 0 && !showRetryingHint){
                refreshDeliveryTaskOrderList();
            }
        }, 100);

        // 清理定时器，防止内存泄漏
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        sessionStorage.removeItem('returningFromDetail')
        if (!deliveringTasks?.length) {
            setOrders([]);
            return;
        }
        const deliveringData = deliveringTasks.filter(item => {
        return (item.isPickedUp || item.isAbnormal || item.isDelivery) && !item.isTransferOut }) || [];
        if (!deliveringData?.length){
            setOrders([]);
            return;
        }
    
        const actions = exceptionDictionaries;
        // @ts-ignore
        // @ts-ignore
        const formattedData = deliveringData.map(item => {
            const exceptionReasonCode = item.orderWithAddress?.order?.exceptionReasonCode;
            const exceptionReason = actions.find(
                action => action.exceptionReasonCode === exceptionReasonCode
            );

            return {
                id: item.id,
                trackingNumber: item.trackingNumber || '',
                deliveryAttempts: item.orderWithAddress?.order?.deliveryAttempts || 0,
                stopNumber: item.stopNumber,
                subStopNumber: item.subStopNumber,
                fullName: item.orderWithAddress?.deliveryAddress?.fullName || '',
                address: [
                    item.orderWithAddress?.deliveryAddress?.addressLine1 || '',
                    item.orderWithAddress?.deliveryAddress?.addressLine2 || '',
                    item.orderWithAddress?.deliveryAddress?.city || '',
                    item.orderWithAddress?.deliveryAddress?.state || '',
                    item.orderWithAddress?.deliveryAddress?.zipcode || '',
                    'US'
                ].filter(Boolean).join(' '),
                routeNumber: item.routeName,
                deliveredAt: item.orderWithAddress?.order?.deliveredAt || '',
                expectedDeliveryTime: item.orderWithAddress?.order?.expectedDeliveryTime || '',
                deliveryType: item?.orderWithAddress?.order?.overallStatus || '',
                phone: item.orderWithAddress?.deliveryAddress?.phoneNumber || '',
                isPickUp:item?.isPickedUp,
                isAbnormal: item.isAbnormal,
                isDelivered: item?.orderWithAddress?.order?.isDelivered || false,
                exceptionReason: exceptionReason,
                isTransferIn: item.isTransferIn,
                isReturnToWhsScanned: item?.orderWithAddress?.order?.isReturnToWhsScanned,
                isDelivery:item?.isDelivery,
                isFinished: item?.orderWithAddress?.order?.isFinished,
                deliveryTaskId: item.deliveryTaskId,
                createdAt: item.deliveryTaskType?.createdAt || '',
                routeHistoryId: item.deliveryTaskType?.routeHistoryId || 0,
                routeName: item.routeName
            };
        }).sort((a, b) => sortDeliveryOrders(a, b, sameAreaEnabledData?.checkSameAreaEnabled));
        setOrders(formattedData);
    }, [deliveringTasks, activeTab, sameAreaEnabledData?.checkSameAreaEnabled]);

    useEffect(() => {
        const newDeliveringCount = orders.filter(order =>
            order.isPickUp && !order.isAbnormal && order.isDelivery && !order.isDelivered && !order.isFinished
        ).length;

        const newUndeliveredCount = orders.filter(order =>
            order.isAbnormal && !order.isReturnToWhsScanned && order.isDelivery && !order.isDelivered && !order.isFinished
        ).length;

        setDeliveringCount(newDeliveringCount);
        setUndeliveredCount(newUndeliveredCount);
    }, [orders]);

    useEffect(() => {
        if (showRetryingHint) {
            setUploadStatus('retrying');
        } else if (uploadStatus === 'retrying') {
            setUploadStatus('success');
            refreshFailedCount();
        }
    }, [showRetryingHint, uploadStatus, refreshFailedCount]);

    const triggerRetry = useCallback(() => {
        if (failedUploadCount > 0 && !isRetrying) {
            handleRetry();
        }
    }, [failedUploadCount, isRetrying, handleRetry]);

    const handleNotificationClick = useCallback(() => {
        if (uploadStatus === 'success') {
            setUploadStatus('none');
        } else if (uploadStatus === 'none' && failedUploadCount > 0) {
            triggerRetry();
        }
    }, [uploadStatus, failedUploadCount, triggerRetry]);

    const filteredOrders = orders.filter(order =>
        (activeTab === 'delivering' && order.isPickUp && !order.isAbnormal && order.isDelivery && !order.isDelivered && !order.isFinished)
        || (activeTab === 'undelivered' && order.isAbnormal && !order.isReturnToWhsScanned && order.isDelivery && !order.isDelivered && !order.isFinished)
    ).filter(order =>
        order.trackingNumber.toLowerCase().includes(searchValue.toLowerCase())
    );


    // 根据stopNumber前缀分组订单
    const groupOrdersByStopPrefix = (orders: DeliveringOrder[]) => {
        const groups: { [prefix: string]: DeliveringOrder[] } = {};

        orders.forEach(order => {
            const prefix = order.address;
            if (!groups[prefix]) {
                groups[prefix] = [];
            }
            groups[prefix].push(order);
        });

        return groups;
    };

    const groupedOrders = groupOrdersByStopPrefix(filteredOrders);

    const tabs: DeliveryTab[] = [
        {
            key: 'delivering',
            title: `${t('pages.delivery.delivering.deliveringTab')}`,
            count: deliveringCount
        },
        {
            key: 'undelivered',
            title: `${t('pages.delivery.delivering.unDeliveredTab')}`,
            count: undeliveredCount
        }
    ];

    const handleTabChange = (key: string) => {
        setActiveTab(key);
    };

    const handleSearch = async (value: string) => {
        setSearchValue(value);
    };

    // 恢复主容器滚动位置
    useEffect(() => {
        if (!loading && orders.length > 0) {
            const savedScrollTop = sessionStorage.getItem('deliveringPageScrollTop');
            if (savedScrollTop && mainContainerRef.current) {
                setTimeout(() => {
                    if (mainContainerRef.current) {
                        mainContainerRef.current.scrollTop = parseInt(savedScrollTop);
                        sessionStorage.removeItem('deliveringPageScrollTop');
                    }
                }, 200);
            }
        }
    }, [loading, orders]);

    const handleRefresh = async () => {
        if (!isOnline) {
            Toast.show({
                content: 'Network connection unavailable',
                position: 'bottom'
            });
            return;
        }

        setRefreshing(true);
        try {
            await refreshDeliveryTaskOrderList();
            await refreshFailedCount();
        } catch (error) {
            Toast.show({
                content: 'Refresh failed, please try again later',
                position: 'bottom'
            });
        } finally {
            setRefreshing(false);
        }
    };

    return (
            <PullToRefreshWrapper onRefresh={handleRefresh}>
                <div id="delivering-main-container" className="flex flex-col h-screen bg-gray-50 gap-2 p-2 overflow-y-auto pb-20" ref={mainContainerRef} style={{ paddingBottom: `calc(80px + ${podAlertHeight}px + env(safe-area-inset-bottom))` }}>
                    <UploadNotification
                        status={uploadStatus}
                        onClick={handleNotificationClick}
                    />

                    <SearchBar onSearch={handleSearch}/>

                    <DeliveryTabs
                        tabs={tabs}
                        activeKey={activeTab}
                        onChange={handleTabChange}
                    />


                    <div className="flex-1">
                        {loading && !refreshing ? (
                            <div className="flex flex-col h-screen">
                                <Skeleton.Title animated />
                                <Skeleton.Paragraph lineCount={3} animated />
                                <Skeleton.Title animated />
                                <Skeleton.Paragraph lineCount={3} animated />
                                <Skeleton.Title animated />
                                <Skeleton.Paragraph lineCount={3} animated />
                                <Skeleton.Title animated />
                                <Skeleton.Paragraph lineCount={3} animated />
                                <Skeleton.Title animated />
                                <Skeleton.Paragraph lineCount={3} animated />
                            </div>
                        ):(
                            <>
                                {filteredOrders.length > 0 ? (
                                    <div>
                                        {Object.entries(groupedOrders).map(([stopNumber, ordersInGroup]) => {
                                            if (ordersInGroup.length === 1) {
                                                return (
                                                    <OrderCard
                                                        key={ordersInGroup[0].id}
                                                        order={ordersInGroup[0]}
                                                        activeTab={activeTab}
                                                    />
                                                );
                                            }

                                            return (
                                                <div
                                                    key={stopNumber}
                                                    className="border-2 border-blue-400 rounded-lg mb-3 relative bg-blue-50"
                                                >
                                                    <div className="absolute top-0 left-0 bg-blue-400 text-white text-xs px-2 py-1 rounded-br z-10 whitespace-nowrap">
                                                        {t('pages.delivery.delivering.addressGroup', {orderCount: ordersInGroup.length})}
                                                    </div>
`
                                                    {ordersInGroup.map((order) => (
                                                        <OrderCard
                                                            key={order.id}
                                                            order={order}
                                                            activeTab={activeTab}
                                                        />
                                                    ))}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <EmptyState/>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </PullToRefreshWrapper>
    );
};


const DeliveringPage: React.FC = () => {
    const { isOnline } = useNetwork();

    return (
        <DeliveryLayout title={`${t('pages.delivery.delivering.title')}`}>
            {isOnline && <BarCodeScanner showManualInputIcon={false}/>}
            <Body/>
        </DeliveryLayout>
    );
};

export default DeliveringPage;